/**
 * Tenant Organization Settings Component — shadcn/Tailwind
 * Tenant-specific settings using /v1/core/organization/ endpoints
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Separator } from '../../components/ui/separator';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../../components/ui/tabs';
import { toast } from '../../components/ui/use-toast';
import {
  Settings, Save, Globe, FlaskConical, Shield, Bell, Plug, Info,
  Stethoscope, Dog, User, Check, AlertTriangle, Truck, Package,
} from 'lucide-react';
import { authenticatedApi } from '../../utils/authenticatedApi';
import {
  fetchCommonTimezones, fetchCommonLanguages, fetchCommonDateFormats, fetchCommonTimeFormats,
  type TimezoneOption, type LanguageOption, type DateFormatOption, type TimeFormatOption,
} from '../../utils/regionalSettings';
import { PageHeader } from '../ui/page-header';

// Types
interface OrganizationChoices {
  organization_type: string[]; laboratory_type: string[]; industry_sector: string[];
  report_delivery_method: string[]; report_template: string[];
  alert_types: { value: string; label: string }[];
  default_sample_type: string[];
  /** 'tenant' when the list is this lab's own sample-type register. */
  default_sample_type_source: 'tenant' | 'product';
}

interface TenantOrganizationProfile {
  id?: string | null;
  /** False when this tenant has no Company row at all. */
  configured: boolean;
  detail?: string;
  /** Blocks the tenant has never saved — shown as product defaults, labelled. */
  defaulted_blocks: string[];
  choices: OrganizationChoices;
  legal_name: string; description: string; tax_id: string; registration_number: string;
  vat_registered: boolean; vat_number: string;
  organization_name: string; organization_code: string; organization_type: string;
  industry_sector: string; laboratory_type: string; accreditation_body: string;
  accreditation_number: string; license_number: string;
  primary_email: string; secondary_email: string; primary_phone: string;
  fax_number: string; website_url: string;
  street_address: string; city: string; state_province: string; postal_code: string; country: string;
  lab_settings: {
    default_sample_type: string; auto_generate_sample_ids: boolean; sample_id_prefix: string;
    require_sample_temperature: boolean; enable_barcode_scanning: boolean;
    enable_rfid_tracking: boolean; chain_of_custody_required: boolean; quality_control_enabled: boolean;
  };
  workflow_settings: {
    auto_assign_technicians: boolean; require_supervisor_approval: boolean;
    enable_priority_samples: boolean; default_turnaround_time_hours: number;
    urgent_turnaround_time_hours: number; enable_batch_processing: boolean; max_batch_size: number;
  };
  reporting_settings: {
    auto_generate_reports: boolean; report_delivery_method: string; include_qr_codes: boolean;
    include_digital_signature: boolean; watermark_reports: boolean; report_template: string;
    custom_footer_text: string;
  };
  security_settings: {
    password_policy: { min_length: number; require_uppercase: boolean; require_lowercase: boolean; require_numbers: boolean; require_symbols: boolean; expiry_days: number };
    session_timeout_minutes: number; max_failed_login_attempts: number;
    two_factor_required: boolean; audit_log_retention_days: number;
  };
  notification_settings: {
    email_notifications: boolean; sms_notifications: boolean; push_notifications: boolean;
    slack_notifications: boolean; webhook_notifications: boolean;
    alert_types: string[]; notification_channels: any[];
  };
  integration_settings: {
    enable_lis_integration: boolean; enable_hl7_interface: boolean; enable_fhir_api: boolean;
    api_rate_limit_per_minute: number; webhook_retry_attempts: number; custom_field_mapping: boolean;
  };
  timezone: string; language: string; date_format: string; time_format: string;
  /** Read-only here: the lab's currency is owned by Core Settings → Currency Management. */
  currency: string; number_format: string;
  created_at?: string | null; updated_at?: string | null;
}

/* There is deliberately NO default profile here.
 *
 * There used to be: "Laboratory Services", "LAB001", "Clinical Laboratory",
 * "Healthcare", Indian/Mauritius, MUR. The endpoint this screen calls did not
 * exist, so every load 404'd into the `catch` below and painted those values —
 * and every tenant that opened the page saw the SAME invented laboratory,
 * indistinguishable from its own data, with a Save button that 404'd.
 *
 * A screen must not be able to invent a lab's identity. The profile now comes
 * from /v1/core/organization/profile/ or the screen shows that it could not be
 * loaded. Settings that nobody has chosen yet fall back to product defaults
 * held ONCE on the server (core/views_organization_profile.PRODUCT_DEFAULTS),
 * which arrive labelled via `defaulted_blocks` so the screen can say so rather
 * than present them as this lab's decisions.
 */
// Currency management moved to its own sidebar page (Core Settings →
// Currency Management, /core/currencies) — no tab here anymore.

/* ── Laboratory Scope ─────────────────────────────────────────────────────
 * Who this lab tests: people, animals, or both. LabBOXX ships ONE catalog
 * carrying both menus, so a purely human lab otherwise reads — and can order
 * — the veterinary tests it will never run.
 *
 * This block loads and saves on its OWN endpoint (/v1/core/lab-scope/), not
 * with the Save Settings button below it. The scope decides what the Test
 * Catalog offers, and it must not be coupled to the rest of this form: one
 * dropdown that changes what the lab may order deserves its own explicit act
 * of saving, and its own confirmation of what that did.
 */
type LabMode = 'human' | 'veterinary' | 'both';

interface LabScopeChoice { value: LabMode; label: string; enables: number }

interface LabScopeState {
  lab_mode: LabMode;
  lab_mode_label: string;
  /** This lab is the in-house laboratory of the system that orders from it:
   *  accessioning a partner order also marks it collected and received. */
  partner_inhouse: boolean;
  /** A partner order's tubes come off THIS lab's inventory when collected
   *  (default off: a clinic-drawn tube came off the clinic's shelf). */
  partner_tube_stock: boolean;
  configured: boolean;
  detail?: string;
  choices: LabScopeChoice[];
  catalog: {
    total_active: number;
    by_type: Record<string, number>;
    enabled: number;
    disabled_by_scope: number;
  };
}

const LAB_MODE_ICON: Record<LabMode, React.ReactNode> = {
  human: <User className="h-4 w-4" />,
  veterinary: <Dog className="h-4 w-4" />,
  both: <FlaskConical className="h-4 w-4" />,
};

const LAB_MODE_BLURB: Record<LabMode, string> = {
  human: 'Patients only. Veterinary tests stay on file but are switched off.',
  veterinary: 'Animals only. Human-only tests stay on file but are switched off.',
  both: 'Hybrid lab — the whole catalog stays available.',
};

// Main Component
const TenantOrganizationSettings: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  // null until the server answers. There is no local stand-in to fall back to
  // — see the note above the type.
  const [profile, setProfile] = useState<TenantOrganizationProfile | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [timezones, setTimezones] = useState<TimezoneOption[]>([]);
  const [languages, setLanguages] = useState<LanguageOption[]>([]);
  const [dateFormats, setDateFormats] = useState<DateFormatOption[]>([]);
  const [timeFormats, setTimeFormats] = useState<TimeFormatOption[]>([]);
  const [labScope, setLabScope] = useState<LabScopeState | null>(null);
  const [labMode, setLabMode] = useState<LabMode>('both');
  const [labScopeSaving, setLabScopeSaving] = useState(false);

  const loadRegionalData = async () => {
    try {
      const [tz, lang, df, tf] = await Promise.all([fetchCommonTimezones(), fetchCommonLanguages(), fetchCommonDateFormats(), fetchCommonTimeFormats()]);
      setTimezones(tz); setLanguages(lang); setDateFormats(df); setTimeFormats(tf);
    } catch { toast({ title: 'Failed to load regional settings', variant: 'destructive' }); }
  };

  const loadProfile = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await authenticatedApi.get('/v1/core/organization/profile/');
      if (response.error || !response.data) throw new Error(response.error || 'No response from the server.');
      setProfile(response.data);
    } catch (error: any) {
      // Deliberately leaves the form UNRENDERED rather than showing stand-in
      // values: a failed load must look like a failed load.
      setProfile(null);
      setLoadError(error?.message || 'Could not load this laboratory’s profile.');
    } finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!profile) return;
    setLoading(true);
    try {
      const response = await authenticatedApi.post('/v1/core/organization/profile/', profile);
      if (response.error) throw new Error(response.error);
      toast({ title: 'Settings saved', description: profile.organization_name || undefined, variant: 'success' });
      setProfile(response.data);
    } catch (error: any) {
      const msg = error.response?.data?.detail || error.message || 'Failed to save settings';
      toast({ title: 'Save failed', description: msg, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const loadLabScope = async () => {
    try {
      const response = await authenticatedApi.get('/v1/core/lab-scope/');
      if (response.error || !response.data) return;
      setLabScope(response.data);
      setLabMode(response.data.lab_mode);
    } catch { /* the card simply does not render until it loads */ }
  };

  const saveLabScope = async () => {
    setLabScopeSaving(true);
    try {
      const response = await authenticatedApi.put('/v1/core/lab-scope/', { lab_mode: labMode });
      if (response.error) throw new Error(response.error);
      setLabScope(response.data);
      setLabMode(response.data.lab_mode);
      const { enabled, disabled_by_scope: off } = response.data.catalog;
      toast({
        title: `Laboratory scope: ${response.data.lab_mode_label}`,
        description: off > 0
          ? `${enabled} tests enabled · ${off} disabled in the Test Catalog.`
          : `All ${enabled} active tests enabled.`,
        variant: 'success',
      });
    } catch (error: any) {
      toast({
        title: 'Could not change the laboratory scope',
        description: error?.message || 'Please try again.',
        variant: 'destructive',
      });
    } finally { setLabScopeSaving(false); }
  };

  /* The partner in-house toggle saves on the click: one switch, one fact, no
     Apply button to forget. It rides the lab-scope endpoint because that is
     the tenant's single lab-settings row. */
  const [partnerInhouseSaving, setPartnerInhouseSaving] = useState(false);
  const savePartnerInhouse = async (value: boolean) => {
    setPartnerInhouseSaving(true);
    try {
      const response = await authenticatedApi.patch('/v1/core/lab-scope/', { partner_inhouse: value });
      if (response.error) throw new Error(response.error);
      setLabScope(response.data);
      setLabMode(response.data.lab_mode);
      toast({
        title: value ? 'Partner in-house lab: ON' : 'Partner in-house lab: OFF',
        description: value
          ? 'Accessioning an order from a connected system now also marks it collected and received — next step Start Testing.'
          : 'Orders from connected systems walk Accession → Mark Collected → Receive in Lab again.',
        variant: 'success',
      });
    } catch (error: any) {
      toast({ title: 'Could not change the partner in-house setting', description: error?.message || 'Please try again.', variant: 'destructive' });
    } finally { setPartnerInhouseSaving(false); }
  };

  /* Whose shelf a partner order's tubes came off. Same endpoint, same
     save-on-click; separate from the in-house switch because a lab can be
     the clinic's in-house lab and still not supply its tubes. */
  const [partnerTubeStockSaving, setPartnerTubeStockSaving] = useState(false);
  const savePartnerTubeStock = async (value: boolean) => {
    setPartnerTubeStockSaving(true);
    try {
      const response = await authenticatedApi.patch('/v1/core/lab-scope/', { partner_tube_stock: value });
      if (response.error) throw new Error(response.error);
      setLabScope(response.data);
      setLabMode(response.data.lab_mode);
      toast({
        title: value ? 'Partner tube stock: ON' : 'Partner tube stock: OFF',
        description: value
          ? 'Collecting a partner order now issues its tubes from this lab’s inventory (Container Types linked to an item).'
          : 'A partner order’s tubes are the clinic’s — nothing is issued from this lab’s stock.',
        variant: 'success',
      });
    } catch (error: any) {
      toast({ title: 'Could not change the partner tube stock setting', description: error?.message || 'Please try again.', variant: 'destructive' });
    } finally { setPartnerTubeStockSaving(false); }
  };

  useEffect(() => { loadProfile(); loadRegionalData(); loadLabScope(); }, []);

  const updateField = (path: string, value: any) => {
    setProfile((prev) => {
      if (!prev) return prev;
      const copy = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = copy;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return copy;
    });
  };

  /** "These are product defaults, not this lab's choices" — shown per tab. */
  const DefaultsNote = ({ block, detail }: { block: string; detail?: string }) => (
    profile?.defaulted_blocks?.includes(block) ? (
      <div className="flex items-start gap-2 rounded-md border border-warning-200 bg-warning-100 p-3">
        <Info className="h-4 w-4 text-warning-700 mt-0.5 shrink-0" />
        <p className="text-sm text-warning-700">
          {detail || (
            <>
              This laboratory has not configured these yet — the values below are LabBOXX
              product defaults. Save to make them this lab’s own.
            </>
          )}
        </p>
      </div>
    ) : null
  );

  /* An option list that arrives EMPTY looks exactly like a working dropdown,
   * which is how four blank Regional selects went unexplained: the master
   * tables behind /v1/platform/regional/ had never been seeded, and an empty
   * 200 is not an error, so the fallback lists below never fired either.
   * Say so on screen instead of rendering an empty menu. */
  const missingLists = [
    timezones.length ? '' : 'timezones',
    languages.length ? '' : 'languages',
    dateFormats.length ? '' : 'date formats',
    timeFormats.length ? '' : 'time formats',
  ].filter(Boolean);

  /* Hoisted OUT of the tabs on purpose: this card saves on its own
   * endpoint, so it must stay usable even when the organisation profile
   * below it cannot be loaded. */
  const LabScopeCard = () => {
    if (!labScope) return null;
    return (
            <Card className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    <Stethoscope className="h-4 w-4" />Laboratory Scope — Human / Veterinary
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
                    Who this lab tests. Test Catalog rows outside the scope are kept — with their
                    prices and reference ranges — but disabled: not orderable, not offered to
                    connected systems. Switch the scope back and they return unchanged.
                  </p>
                </div>
                <Badge variant={labScope.lab_mode === 'both' ? 'info' : 'success'}>
                  {labScope.lab_mode_label}
                </Badge>
              </div>

              {!labScope.configured && (
                <div className="mt-4 flex items-start gap-2 rounded-md border border-warning-200 bg-warning-100 p-3">
                  <AlertTriangle className="h-4 w-4 text-warning-700 mt-0.5" />
                  <p className="text-sm text-warning-700">{labScope.detail}</p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-3 mt-4">
                {labScope.choices.map((choice) => {
                  const selected = labMode === choice.value;
                  return (
                    <button
                      key={choice.value}
                      type="button"
                      onClick={() => setLabMode(choice.value)}
                      className={`text-left rounded-md border p-4 transition-colors ${
                        selected
                          ? 'border-primary bg-primary/5 ring-2 ring-primary/30'
                          : 'border-border hover:bg-muted/50'
                      }`}
                    >
                      <div className="flex items-center gap-2 font-medium">
                        {LAB_MODE_ICON[choice.value]}
                        <span>{choice.label}</span>
                        {selected && <Check className="h-4 w-4 text-primary ml-auto" />}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{LAB_MODE_BLURB[choice.value]}</p>
                      <p className="text-xs font-medium mt-2">
                        Enables {choice.enables} of {labScope.catalog.total_active} active tests
                      </p>
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center justify-between gap-4 mt-4">
                <p className="text-sm text-muted-foreground">
                  {labScope.catalog.disabled_by_scope > 0
                    ? `Right now ${labScope.catalog.enabled} tests are enabled and ${labScope.catalog.disabled_by_scope} are disabled by this scope.`
                    : `Right now all ${labScope.catalog.total_active} active tests are enabled.`}
                </p>
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" onClick={() => navigate('/laboratory/test-catalog')}>
                    Open Test Catalog
                  </Button>
                  <Button
                    onClick={saveLabScope}
                    disabled={labScopeSaving || labMode === labScope.lab_mode}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {labMode === labScope.lab_mode ? 'Scope saved' : 'Apply scope'}
                  </Button>
                </div>
              </div>
            </Card>
    );
  };

  /* Same row as the scope, own card: a lab that is the in-house laboratory
     of the system ordering from it (a clinic's PetsBOXX → the clinic's own
     lab). Partner specimens arrive already drawn and labelled, so one
     Accession click stands for collect + receive as well. */
  const PartnerInhouseCard = () => {
    if (!labScope || !labScope.configured) return null;
    return (
      <Card className="p-6">
        <div className="flex items-start justify-between gap-6">
          <div>
            <h3 className="font-semibold flex items-center gap-2">
              <Truck className="h-4 w-4" />Partner In-house Lab
            </h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
              Switch on when this laboratory is the in-house lab of the system that sends it orders
              (for example a clinic’s PetsBOXX). Those specimens are drawn and labelled at the clinic
              before they reach the bench, so the <strong>Accession</strong> click also marks the order
              collected and received and the next step is <strong>Start Testing</strong>. Orders placed
              here in LabBOXX keep the full Accession → Collect → Receive path either way.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0 pt-1">
            <Badge variant={labScope.partner_inhouse ? 'success' : 'default'}>
              {labScope.partner_inhouse ? 'ON' : 'OFF'}
            </Badge>
            <Switch
              checked={!!labScope.partner_inhouse}
              disabled={partnerInhouseSaving}
              onCheckedChange={(v) => savePartnerInhouse(v)}
              aria-label="Partner in-house lab"
            />
          </div>
        </div>
        <div className="flex items-start justify-between gap-6 border-t mt-5 pt-5">
          <div>
            <h4 className="font-medium flex items-center gap-2">
              <Package className="h-4 w-4" />Partner orders consume this lab’s tube stock
            </h4>
            <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
              Off: a partner order’s tubes were drawn from the clinic’s own shelf, so nothing leaves
              this lab’s inventory. On: collecting a partner order (the Accession click, when Partner
              In-house Lab is on) issues one unit per tube against the inventory item linked to each
              Container Type — the same as an order placed here. Orders placed in LabBOXX always
              issue from this lab’s stock.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0 pt-1">
            <Badge variant={labScope.partner_tube_stock ? 'success' : 'default'}>
              {labScope.partner_tube_stock ? 'ON' : 'OFF'}
            </Badge>
            <Switch
              checked={!!labScope.partner_tube_stock}
              disabled={partnerTubeStockSaving}
              onCheckedChange={(v) => savePartnerTubeStock(v)}
              aria-label="Partner orders consume this lab's tube stock"
            />
          </div>
        </div>
      </Card>
    );
  };

  const SaveButton = () => (
    <div className="flex justify-end gap-2 mt-6">
      <Button variant="outline" onClick={loadProfile}>Reset</Button>
      <Button onClick={handleSave} disabled={loading}><Save className="h-4 w-4 mr-2" />Save Settings</Button>
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={profile?.organization_name || 'Organization Settings'}
        icon={<FlaskConical className="h-6 w-6" />}
        description={profile?.organization_name
          ? `Organisation record, workflow preferences and operational parameters for ${profile.organization_name}.`
          : 'Configure laboratory organization settings, workflow preferences, and operational parameters.'}
      />

      <LabScopeCard />
      <PartnerInhouseCard />

      {/* Loaded, failed, or "this tenant has no organisation record" — each
          says which. The form is NOT rendered on a failure: a screen full of
          plausible values is indistinguishable from real data. */}
      {!profile ? (
        <Card className="p-6">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading this laboratory’s profile…</p>
          ) : (
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-destructive mt-0.5" />
                <div>
                  <p className="font-medium">Organisation profile could not be loaded</p>
                  <p className="text-sm text-muted-foreground mt-1">{loadError}</p>
                </div>
              </div>
              <Button variant="outline" onClick={loadProfile}>Try again</Button>
            </div>
          )}
        </Card>
      ) : (
      <>
      {profile.configured ? (
        <div className="bg-info-100 border border-info-200 rounded-md p-3 flex items-center gap-2">
          <Info className="h-4 w-4 text-info" />
          <p className="text-sm text-foreground">
            Editing <strong>{profile.organization_name || profile.organization_code || 'this laboratory'}</strong>
            {profile.organization_code && profile.organization_name ? ` (${profile.organization_code})` : ''} — saved to this tenant’s own database.
          </p>
        </div>
      ) : (
        <div className="bg-warning-100 border border-warning-200 rounded-md p-3 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-warning-700 mt-0.5" />
          <p className="text-sm text-warning-700">{profile.detail}</p>
        </div>
      )}

      <Tabs defaultValue="general">
        <TabsList className="grid w-full grid-cols-8">
          <TabsTrigger value="general"><Settings className="h-3 w-3 mr-1" />General</TabsTrigger>
          <TabsTrigger value="laboratory"><FlaskConical className="h-3 w-3 mr-1" />Lab</TabsTrigger>
          <TabsTrigger value="workflow"><Settings className="h-3 w-3 mr-1" />Workflow</TabsTrigger>
          <TabsTrigger value="reporting"><Bell className="h-3 w-3 mr-1" />Reports</TabsTrigger>
          <TabsTrigger value="regional"><Globe className="h-3 w-3 mr-1" />Regional</TabsTrigger>
          <TabsTrigger value="security"><Shield className="h-3 w-3 mr-1" />Security</TabsTrigger>
          <TabsTrigger value="notifications"><Bell className="h-3 w-3 mr-1" />Alerts</TabsTrigger>
          <TabsTrigger value="integrations"><Plug className="h-3 w-3 mr-1" />Integrate</TabsTrigger>
        </TabsList>

        {/* General */}
        <TabsContent value="general" className="space-y-4 mt-4">
          <Card className="p-6">
            <h3 className="font-semibold mb-4">Laboratory Identity</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Laboratory Name *</Label><Input value={profile.organization_name} onChange={(e) => updateField('organization_name', e.target.value)} /></div>
              <div className="space-y-2"><Label>Laboratory Code *</Label><Input value={profile.organization_code} onChange={(e) => updateField('organization_code', e.target.value)} maxLength={10} /></div>
              <div className="space-y-2"><Label>Laboratory Type *</Label>
                <Select value={profile.laboratory_type} onValueChange={(v) => updateField('laboratory_type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {profile.choices.laboratory_type.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Industry Sector *</Label>
                <Select value={profile.industry_sector} onValueChange={(v) => updateField('industry_sector', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {profile.choices.industry_sector.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Accreditation Body</Label><Input value={profile.accreditation_body} onChange={(e) => updateField('accreditation_body', e.target.value)} placeholder="CAP, CLIA, ISO 15189" /></div>
              <div className="space-y-2"><Label>Accreditation Number</Label><Input value={profile.accreditation_number} onChange={(e) => updateField('accreditation_number', e.target.value)} /></div>
            </div>
            {/* Three different numbers, and only ONE of them prints. The lab had
                no field for any of them, so the document engine read the VAT
                number off the Tax ID and unregistered labs issued "VAT INVOICE"
                slips carrying a registration they do not hold. */}
            <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t">
              <div className="space-y-2">
                <Label>Business Registration No. (BRN)</Label>
                <Input value={profile.registration_number}
                       onChange={(e) => updateField('registration_number', e.target.value)}
                       placeholder="e.g. C19165676" />
                <p className="text-xs text-muted-foreground">Prints on invoices as BRN.</p>
              </div>
              <div className="space-y-2">
                <Label>Tax Account No. (TAN)</Label>
                <Input value={profile.tax_id}
                       onChange={(e) => updateField('tax_id', e.target.value)}
                       placeholder="MRA tax account number" />
                <p className="text-xs text-muted-foreground">
                  A different thing from a VAT number, and never printed on a document.
                </p>
              </div>
              <div className="space-y-2">
                <Label>VAT registered</Label>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={!!profile.vat_registered}
                    onChange={(e) => {
                      // Switching registration off clears the number: an
                      // unregistered lab HAS no VAT number, and a stale one prints.
                      updateField('vat_registered', e.target.checked)
                      if (!e.target.checked) updateField('vat_number', '')
                    }}
                  />
                  <span className="text-muted-foreground">
                    Off: documents print as "INVOICE" with no VAT number and no VAT analysis.
                  </span>
                </label>
              </div>
              <div className="space-y-2">
                <Label>VAT Number</Label>
                <Input value={profile.vat_number}
                       disabled={!profile.vat_registered}
                       onChange={(e) => updateField('vat_number', e.target.value)}
                       placeholder="VAT registration number" />
                <p className="text-xs text-muted-foreground">
                  The only tax number a document carries.
                </p>
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-2 gap-4">
            <Card className="p-6">
              <h3 className="font-semibold mb-4">Contact Information</h3>
              <div className="space-y-3">
                <div className="space-y-2"><Label>Primary Email</Label><Input type="email" value={profile.primary_email} onChange={(e) => updateField('primary_email', e.target.value)} /></div>
                <div className="space-y-2"><Label>Secondary Email</Label><Input type="email" value={profile.secondary_email} onChange={(e) => updateField('secondary_email', e.target.value)} /></div>
                <div className="space-y-2"><Label>Primary Phone</Label><Input value={profile.primary_phone} onChange={(e) => updateField('primary_phone', e.target.value)} /></div>
                <div className="space-y-2"><Label>Fax</Label><Input value={profile.fax_number} onChange={(e) => updateField('fax_number', e.target.value)} /></div>
                <div className="space-y-2"><Label>Website</Label><Input value={profile.website_url} onChange={(e) => updateField('website_url', e.target.value)} /></div>
              </div>
            </Card>
            <Card className="p-6">
              <h3 className="font-semibold mb-4">Address</h3>
              <div className="space-y-3">
                <div className="space-y-2"><Label>Street Address</Label><Textarea rows={2} value={profile.street_address} onChange={(e) => updateField('street_address', e.target.value)} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>City</Label><Input value={profile.city} onChange={(e) => updateField('city', e.target.value)} /></div>
                  <div className="space-y-2"><Label>State/Province</Label><Input value={profile.state_province} onChange={(e) => updateField('state_province', e.target.value)} /></div>
                  <div className="space-y-2"><Label>Postal Code</Label><Input value={profile.postal_code} onChange={(e) => updateField('postal_code', e.target.value)} /></div>
                  <div className="space-y-2"><Label>Country</Label><Input value={profile.country} onChange={(e) => updateField('country', e.target.value)} /></div>
                </div>
              </div>
            </Card>
          </div>
          <SaveButton />
        </TabsContent>

        {/* Laboratory */}
        <TabsContent value="laboratory" className="space-y-4 mt-4">
          <DefaultsNote block="lab_settings" />
          <Card className="p-6">
            <h3 className="font-semibold mb-4">Sample Management</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Default Sample Type</Label>
                <Select value={profile.lab_settings.default_sample_type} onValueChange={(v) => updateField('lab_settings.default_sample_type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {profile.choices.default_sample_type.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Sample ID Prefix</Label><Input value={profile.lab_settings.sample_id_prefix} onChange={(e) => updateField('lab_settings.sample_id_prefix', e.target.value)} maxLength={5} /></div>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              {[
                ['lab_settings.auto_generate_sample_ids', 'Auto-generate Sample IDs', profile.lab_settings.auto_generate_sample_ids],
                ['lab_settings.enable_barcode_scanning', 'Enable Barcode Scanning', profile.lab_settings.enable_barcode_scanning],
                ['lab_settings.chain_of_custody_required', 'Require Chain of Custody', profile.lab_settings.chain_of_custody_required],
                ['lab_settings.quality_control_enabled', 'Enable Quality Control', profile.lab_settings.quality_control_enabled],
                ['lab_settings.require_sample_temperature', 'Require Sample Temperature', profile.lab_settings.require_sample_temperature],
              ].map(([path, label, val]) => (
                <div key={path as string} className="flex items-center gap-2">
                  <Switch checked={val as boolean} onCheckedChange={(v) => updateField(path as string, v)} />
                  <Label className="text-sm">{label as string}</Label>
                </div>
              ))}
            </div>
          </Card>
          <SaveButton />
        </TabsContent>

        {/* Workflow */}
        <TabsContent value="workflow" className="space-y-4 mt-4">
          <DefaultsNote block="workflow_settings" />
          <Card className="p-6">
            <h3 className="font-semibold mb-4">Sample Processing Workflow</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Default Turnaround (hours)</Label><Input type="number" min={1} max={168} value={profile.workflow_settings.default_turnaround_time_hours} onChange={(e) => updateField('workflow_settings.default_turnaround_time_hours', parseInt(e.target.value))} /></div>
              <div className="space-y-2"><Label>Urgent Turnaround (hours)</Label><Input type="number" min={1} max={48} value={profile.workflow_settings.urgent_turnaround_time_hours} onChange={(e) => updateField('workflow_settings.urgent_turnaround_time_hours', parseInt(e.target.value))} /></div>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              {[
                ['workflow_settings.auto_assign_technicians', 'Auto-assign Technicians', profile.workflow_settings.auto_assign_technicians],
                ['workflow_settings.require_supervisor_approval', 'Require Supervisor Approval', profile.workflow_settings.require_supervisor_approval],
                ['workflow_settings.enable_priority_samples', 'Enable Priority Samples', profile.workflow_settings.enable_priority_samples],
                ['workflow_settings.enable_batch_processing', 'Enable Batch Processing', profile.workflow_settings.enable_batch_processing],
              ].map(([path, label, val]) => (
                <div key={path as string} className="flex items-center gap-2">
                  <Switch checked={val as boolean} onCheckedChange={(v) => updateField(path as string, v)} />
                  <Label className="text-sm">{label as string}</Label>
                </div>
              ))}
            </div>
            {profile.workflow_settings.enable_batch_processing && (
              <div className="mt-4 w-48 space-y-2"><Label>Max Batch Size</Label><Input type="number" min={1} max={200} value={profile.workflow_settings.max_batch_size} onChange={(e) => updateField('workflow_settings.max_batch_size', parseInt(e.target.value))} /></div>
            )}
          </Card>
          <SaveButton />
        </TabsContent>

        {/* Reporting */}
        <TabsContent value="reporting" className="space-y-4 mt-4">
          <DefaultsNote block="reporting_settings" />
          <Card className="p-6">
            <h3 className="font-semibold mb-4">Report Generation</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Delivery Method</Label>
                <Select value={profile.reporting_settings.report_delivery_method} onValueChange={(v) => updateField('reporting_settings.report_delivery_method', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{profile.choices.report_delivery_method.map((t) => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Report Template</Label>
                <Select value={profile.reporting_settings.report_template} onValueChange={(v) => updateField('reporting_settings.report_template', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{profile.choices.report_template.map((t) => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              {[
                ['reporting_settings.auto_generate_reports', 'Auto-generate Reports', profile.reporting_settings.auto_generate_reports],
                ['reporting_settings.include_digital_signature', 'Include Digital Signature', profile.reporting_settings.include_digital_signature],
                ['reporting_settings.include_qr_codes', 'Include QR Codes', profile.reporting_settings.include_qr_codes],
                ['reporting_settings.watermark_reports', 'Watermark Reports', profile.reporting_settings.watermark_reports],
              ].map(([path, label, val]) => (
                <div key={path as string} className="flex items-center gap-2">
                  <Switch checked={val as boolean} onCheckedChange={(v) => updateField(path as string, v)} />
                  <Label className="text-sm">{label as string}</Label>
                </div>
              ))}
            </div>
            <div className="mt-4 space-y-2"><Label>Custom Footer Text</Label><Textarea rows={2} value={profile.reporting_settings.custom_footer_text} onChange={(e) => updateField('reporting_settings.custom_footer_text', e.target.value)} /></div>
          </Card>
          <SaveButton />
        </TabsContent>

        {/* Regional */}
        <TabsContent value="regional" className="space-y-4 mt-4">
          <DefaultsNote
            block="regional"
            detail="This laboratory has not chosen its regional settings yet — the values below are LabBOXX defaults, with the timezone taken from this lab’s own address where its country names exactly one. Save to make them this lab’s own."
          />
          {missingLists.length > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3">
              <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
              <p className="text-sm text-destructive">
                No {missingLists.join(', ')} were returned by the server, so those dropdowns
                are empty and nothing can be picked in them. The regional master data is
                missing on this deployment.
              </p>
            </div>
          )}
          <Card className="p-6">
            <h3 className="font-semibold mb-4">Laboratory Localization</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Timezone</Label>
                <Select value={profile.timezone} onValueChange={(v) => updateField('timezone', v)}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{timezones.map((tz) => <SelectItem key={tz.id} value={tz.timezone_id}>{tz.display_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Language</Label>
                <Select value={profile.language} onValueChange={(v) => updateField('language', v)}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{languages.map((l) => <SelectItem key={l.id} value={l.language_code}>{l.english_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Date Format</Label>
                <Select value={profile.date_format} onValueChange={(v) => updateField('date_format', v)}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{dateFormats.map((f) => <SelectItem key={f.id} value={f.format_code}>{f.display_name} ({f.example})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Time Format</Label>
                <Select value={profile.time_format} onValueChange={(v) => updateField('time_format', v)}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{timeFormats.map((f) => <SelectItem key={f.id} value={f.format_code}>{f.display_name} ({f.example})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </Card>
          <SaveButton />
        </TabsContent>

        {/* Security */}
        <TabsContent value="security" className="space-y-4 mt-4">
          <DefaultsNote block="security_settings" />
          <Card className="p-6">
            <h3 className="font-semibold mb-4">Password Policy</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Minimum Length</Label><Input type="number" min={6} max={32} value={profile.security_settings.password_policy.min_length} onChange={(e) => updateField('security_settings.password_policy.min_length', parseInt(e.target.value))} /></div>
              <div className="space-y-2"><Label>Expiry (Days)</Label><Input type="number" min={30} max={365} value={profile.security_settings.password_policy.expiry_days} onChange={(e) => updateField('security_settings.password_policy.expiry_days', parseInt(e.target.value))} /></div>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              {[
                ['security_settings.password_policy.require_uppercase', 'Require Uppercase', profile.security_settings.password_policy.require_uppercase],
                ['security_settings.password_policy.require_lowercase', 'Require Lowercase', profile.security_settings.password_policy.require_lowercase],
                ['security_settings.password_policy.require_numbers', 'Require Numbers', profile.security_settings.password_policy.require_numbers],
              ].map(([path, label, val]) => (
                <div key={path as string} className="flex items-center gap-2">
                  <Switch checked={val as boolean} onCheckedChange={(v) => updateField(path as string, v)} />
                  <Label className="text-sm">{label as string}</Label>
                </div>
              ))}
            </div>
          </Card>
          <Card className="p-6">
            <h3 className="font-semibold mb-4">Session Security</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Session Timeout (min)</Label><Input type="number" min={15} max={1440} value={profile.security_settings.session_timeout_minutes} onChange={(e) => updateField('security_settings.session_timeout_minutes', parseInt(e.target.value))} /></div>
              <div className="space-y-2"><Label>Max Failed Logins</Label><Input type="number" min={3} max={10} value={profile.security_settings.max_failed_login_attempts} onChange={(e) => updateField('security_settings.max_failed_login_attempts', parseInt(e.target.value))} /></div>
              <div className="flex items-center gap-2"><Switch checked={profile.security_settings.two_factor_required} onCheckedChange={(v) => updateField('security_settings.two_factor_required', v)} /><Label>Require 2FA</Label></div>
              <div className="space-y-2"><Label>Audit Log Retention (Days)</Label><Input type="number" min={30} max={2555} value={profile.security_settings.audit_log_retention_days} onChange={(e) => updateField('security_settings.audit_log_retention_days', parseInt(e.target.value))} /></div>
            </div>
          </Card>
          <SaveButton />
        </TabsContent>

        {/* Notifications */}
        <TabsContent value="notifications" className="space-y-4 mt-4">
          <DefaultsNote block="notification_settings" />
          <Card className="p-6">
            <h3 className="font-semibold mb-4">Notification Preferences</h3>
            <div className="grid grid-cols-3 gap-4">
              {[
                ['notification_settings.email_notifications', 'Email', profile.notification_settings.email_notifications],
                ['notification_settings.sms_notifications', 'SMS', profile.notification_settings.sms_notifications],
                ['notification_settings.push_notifications', 'Push', profile.notification_settings.push_notifications],
              ].map(([path, label, val]) => (
                <div key={path as string} className="flex items-center gap-2">
                  <Switch checked={val as boolean} onCheckedChange={(v) => updateField(path as string, v)} />
                  <Label>{label as string} Notifications</Label>
                </div>
              ))}
            </div>
          </Card>
          <Card className="p-6">
            <h3 className="font-semibold mb-4">Alert Types</h3>
            <div className="grid grid-cols-3 gap-3">
              {profile.choices.alert_types.map((at) => (
                <div key={at.value} className="flex items-center gap-2">
                  <Checkbox
                    checked={profile.notification_settings.alert_types.includes(at.value)}
                    onCheckedChange={(checked) => {
                      const types = checked
                        ? [...profile.notification_settings.alert_types, at.value]
                        : profile.notification_settings.alert_types.filter((t) => t !== at.value);
                      updateField('notification_settings.alert_types', types);
                    }}
                  />
                  <Label className="text-sm">{at.label}</Label>
                </div>
              ))}
            </div>
          </Card>
          <SaveButton />
        </TabsContent>

        {/* Integrations */}
        <TabsContent value="integrations" className="space-y-4 mt-4">
          <DefaultsNote block="integration_settings" />
          <Card className="p-6">
            <h3 className="font-semibold mb-4">System Integrations</h3>
            <div className="grid grid-cols-3 gap-4">
              {[
                ['integration_settings.enable_lis_integration', 'Enable LIS Integration', profile.integration_settings.enable_lis_integration],
                ['integration_settings.enable_hl7_interface', 'Enable HL7 Interface', profile.integration_settings.enable_hl7_interface],
                ['integration_settings.enable_fhir_api', 'Enable FHIR API', profile.integration_settings.enable_fhir_api],
                ['integration_settings.custom_field_mapping', 'Custom Field Mapping', profile.integration_settings.custom_field_mapping],
              ].map(([path, label, val]) => (
                <div key={path as string} className="flex items-center gap-2">
                  <Switch checked={val as boolean} onCheckedChange={(v) => updateField(path as string, v)} />
                  <Label className="text-sm">{label as string}</Label>
                </div>
              ))}
            </div>
            <Separator className="my-4" />
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>API Rate Limit (req/min)</Label><Input type="number" min={10} max={1000} value={profile.integration_settings.api_rate_limit_per_minute} onChange={(e) => updateField('integration_settings.api_rate_limit_per_minute', parseInt(e.target.value))} /></div>
              <div className="space-y-2"><Label>Webhook Retry Attempts</Label><Input type="number" min={0} max={10} value={profile.integration_settings.webhook_retry_attempts} onChange={(e) => updateField('integration_settings.webhook_retry_attempts', parseInt(e.target.value))} /></div>
            </div>
          </Card>
          <SaveButton />
        </TabsContent>

      </Tabs>
      </>
      )}
    </div>
  );
};

export default TenantOrganizationSettings;
